import type { OnboardingResponse } from "../api/types";
import type {
  MultiChoiceQuestion,
  OnboardingAnswerValue,
  OnboardingQuestion,
  SingleChoiceQuestion,
} from "../types";
import { isRecord, toMultiChoiceAnswer, toSingleChoiceAnswer } from "./answerHelpers";

const trimOrUndefined = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
};

const normalizeSingleAnswer = (
  question: SingleChoiceQuestion,
  value: OnboardingAnswerValue | undefined
): string | number | string[] | undefined => {
  const parsed = toSingleChoiceAnswer(value);
  if (parsed) {
    if (question.customOption && parsed.choice === question.customOption.value) {
      return trimOrUndefined(parsed.customText) ?? parsed.choice;
    }
    return parsed.choice;
  }

  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  if (isRecord(value) && typeof value.choice === "string") {
    return value.choice;
  }

  return undefined;
};

const normalizeMultiAnswer = (
  question: MultiChoiceQuestion,
  value: OnboardingAnswerValue | undefined
): string[] | undefined => {
  const parsed = toMultiChoiceAnswer(value);
  const selections = parsed?.choices ?? (Array.isArray(value) ? value : []);

  if (!selections.length) return undefined;

  return selections
    .filter((selection): selection is string => typeof selection === "string" && selection.length > 0)
    .map((selection) => {
      if (question.customOption && selection === question.customOption.value) {
        return trimOrUndefined(parsed?.customText) ?? selection;
      }
      return selection;
    });
};

const normalizeAnswer = (
  question: OnboardingQuestion,
  value: OnboardingAnswerValue | undefined
): OnboardingResponse["answer"] | undefined => {
  switch (question.type) {
    case "single":
      return normalizeSingleAnswer(question, value) as
        | string
        | number
        | string[]
        | undefined;
    case "multi":
      return normalizeMultiAnswer(question, value);
    case "slider":
      return typeof value === "number" || typeof value === "string" ? value : undefined;
    case "text":
    case "datepicker":
    case "qrcode":
    case "dynamic_select":
      return typeof value === "string" ? value : undefined;
    default:
      return undefined;
  }
};

export const transformAnswersToResponses = (
  answers: Record<string, unknown>,
  questions: OnboardingQuestion[]
): OnboardingResponse[] => {
  const questionsById = new Map(questions.map((q) => [q.id, q]));

  return Object.entries(answers)
    .map(([questionId, rawValue]) => {
      const question = questionsById.get(questionId);
      if (!question) return null;

      const normalized = normalizeAnswer(
        question,
        rawValue as OnboardingAnswerValue | undefined
      );

      if (normalized === undefined) return null;

      return {
        question_id: questionId,
        answer: normalized,
      } satisfies OnboardingResponse;
    })
    .filter((item): item is OnboardingResponse => item !== null);
};
